/**
 * @file useBodyScrollLock.js
 * @description Locks page scrolling while a full-screen layer (modal, sheet) is open, so touch scrolling
 *   moves the layer and not the page behind it. iOS Safari ignores overflow:hidden on body for touch
 *   scrolling, so the body is pinned with position:fixed at the current offset and restored on unlock.
 *   Nested locks are counted: the page unlocks only when the last layer closes.
 * @module hooks/useBodyScrollLock
 */
import { useEffect } from "react";

let locks = 0;
let saved = null;

function lock() {
  if (locks++ > 0 || typeof document === "undefined") return;
  const body = document.body;
  const scrollY = window.scrollY || window.pageYOffset || 0;
  saved = {
    scrollY,
    overflow: body.style.overflow,
    position: body.style.position,
    top: body.style.top,
    width: body.style.width,
  };
  body.style.overflow = "hidden";
  body.style.position = "fixed";
  body.style.top = `-${scrollY}px`;
  body.style.width = "100%";
}

function unlock() {
  if (locks === 0 || --locks > 0 || typeof document === "undefined" || !saved) return;
  const body = document.body;
  body.style.overflow = saved.overflow;
  body.style.position = saved.position;
  body.style.top = saved.top;
  body.style.width = saved.width;
  window.scrollTo(0, saved.scrollY);
  saved = null;
}

/** The page's real scroll position, also while a modal has it locked (window.scrollY reads 0 then). */
export function pageScrollY() {
  if (saved) return saved.scrollY;
  return typeof window === "undefined" ? 0 : (window.scrollY || window.pageYOffset || 0);
}

/** @param {boolean} active Lock while true. */
export function useBodyScrollLock(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    lock();
    return unlock;
  }, [active]);
}
